import React, { useMemo, useState } from "react";
import { useFormikContext } from "formik";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";

import FieldsIdentifiers from "src/views/capture/FieldsIdentifiers";
import { SustainabilitySheetProvider } from "src/views/capture/SustainabilitySheetContext";
import FieldsOrigin from "src/views/capture/FieldsOrigin";
import FieldsDestination from "src/views/capture/FieldsDestination";
import ItemShipmentInfoFields from "src/components/form/fields/ItemShipmentInfoFields";
import { useFieldSettings } from "src/contexts/customFields";
import useMutation from "src/hooks/useMutation";
import { ADD_ASSET_IMAGE_MUTATION } from "src/constants/graphql";
import { getRestockTotal } from "src/components/modals/InventoryTransactionMultipleModal/Fields";
import { UPDATE_SHIPMENT_ITEMS_MUTATION } from "src/constants/graphql/mutations";
import { GET_SHIPMENT_QUERY, ASSET_QUERY } from "src/constants/graphql/queries";
import useQueryParams from "src/hooks/useQueryParams";
import FieldsPreReceive from "src/views/capture/FieldsPreReceive";
import DraftShipmentModal from "../../Shipment/DraftShipmentModal";
import InventoryPlacementButton from "./InventoryPlacementButton";
import BillingFields from "./BillingFields";
import useEnabledFields from "src/views/capture/hooks/useEnabledFields";
import FieldsSection from "src/views/capture/FieldsSection";

export default function FormFields({
  type,
  disabledForm,
  colorsOptions = [],
  manufacturersOptions = [],
  asset,
  updateAsset,
  setErrors,
  shipmentItem,
  shipmentItemId,
  shipment,
  isDraft,
  isActualDraft,
  isOnsiteInventory,
  onMarkAsReceived,
  sameLocation,
  project,
  overrideDisplayFields = {},
  displayType,
  dispositionOptions = [],
}) {
  const params = useQueryParams();
  const shipmentId = params.get("id");
  const [showStorageFields, setShowStorageFields] = useState(
    !isDraft || (shipmentItem?.quantity ?? 0) > 0
  );

  // Always show inventory placement button when not a draft
  const shouldShowStorageFields = !isDraft || showStorageFields;
  const displayTypeKey = displayType || (type === "item" ? "receivingSettings" : undefined);
  const [createImage] = useMutation(ADD_ASSET_IMAGE_MUTATION);
  const displayFields = useFieldSettings(project, displayTypeKey);
  const display = useMemo(
    () => ({
      ...displayFields,
      // Always show available_quantity for items when not in draft mode to enable Inventory Placement button
      // Exception: Don't show for SKU library (fieldSettings displayType)
      available_quantity:
        type === "item" && !isDraft && displayType !== "fieldSettings"
          ? true
          : displayFields.available_quantity,
      // Allow overrideDisplayFields to have final say
      ...overrideDisplayFields,
      rack_locations: false,
      minimum_quantity: false,
      warehouse_quantity: false,
      in_cart_quantity: false,
      allocated_quantity: false,
      reserved_quantity: false,
      general_notes: false,
      osd_notes: false,
    }),
    [displayFields, overrideDisplayFields, type, isDraft, displayType]
  );
  const { setFieldValue } = useFormikContext();
  const [updateShipmentItem] = useMutation(UPDATE_SHIPMENT_ITEMS_MUTATION, {
    refetchQueries: [
      { query: GET_SHIPMENT_QUERY, variables: { id: shipmentId } },
      { query: ASSET_QUERY, variables: { id: asset.id } },
    ],
  });
  const [showDraftModal, setShowDraftModal] = useState(false);

  const handleUpdateAssetField = field => (value, { onSuccess, onFailure } = {}) => {
    if (field === "barcode") {
      setFieldValue(field, value);
    }
    updateAsset({
      variables: { id: asset.id, input: { [field]: value } },
      onSuccess,
      onFailure: errors => {
        setErrors(errors);
        onFailure && onFailure(errors);
      },
    });
  };

  const handleRestockInventory = (data, values) => {
    // Calculate shipment item quantity as the sum of all rack locations matching this shipment
    const currentShipmentId = shipment?.shipmentId;
    let quantity = 0;

    if (currentShipmentId) {
      // New approach: sum rack locations matching this shipment ID
      quantity = data.asset.rackLocations
        .filter(loc => loc.shipment_id === currentShipmentId)
        .reduce((sum, loc) => sum + (loc.sub_qty || 0), 0);

      // Fallback: if no rack locations match (quantity = 0), use delta-based calculation
      if (quantity === 0) {
        const restockTotal = getRestockTotal(values.locations) - (values.deletedQuantity || 0);
        quantity = shipmentItem.quantity + restockTotal;
      }
    } else {
      // Fallback: if no shipment ID, use delta-based calculation
      const restockTotal = getRestockTotal(values.locations) - (values.deletedQuantity || 0);
      quantity = shipmentItem.quantity + restockTotal;
    }

    setFieldValue("availableQuantity", data.asset.availableQuantity);
    setFieldValue("shipmentItemQuantity", quantity);
    updateShipmentItem({
      variables: {
        id: shipmentItemId,
        quantity,
      },
    });
  };

  const isReceivingWizardDisabled = params.get("disable_receiving_wizard") === "true";
  const { orderedSection: enabledFields } = useEnabledFields({
    display,
    type,
    asset,
    onRestockInventory: handleRestockInventory,
    availableQuantityComponent: displayType === "fieldSettings" ? null : InventoryPlacementButton,
    sameLocation,
    manufacturersOptions,
    colorsOptions,
    dispositionOptions,
    handleUpdateAssetField,
    project,
    disabledForm,
    isReceivingWizardDisabled,
  });

  const handleMarkAsReceived = () => {
    if (isActualDraft || isOnsiteInventory) {
      setShowDraftModal(true);
    } else {
      setShowStorageFields(!showStorageFields);
    }
  };

  const handleKeepAsDraft = () => {
    setShowDraftModal(false);
  };

  const handleConfirmReceived = () => {
    onMarkAsReceived();
    setShowStorageFields(!showStorageFields);
    setShowDraftModal(false);
  };

  return (
    <SustainabilitySheetProvider>
      <Grid container spacing={1}>
        <Grid size={12}>
          <FieldsIdentifiers
            fullWidth
            type={type}
            disabledForm={disabledForm}
            asset={asset}
            fields={enabledFields.identifiers || []}
          />
        </Grid>
        <Grid size={12}>
          <FieldsSection
            title="Descriptive"
            fullWidth
            fields={enabledFields.descriptive || []}
            disabledForm={disabledForm}
          />
        </Grid>
        <Grid size={12}>
          <BillingFields
            fullWidth
            project={project}
            shipment={shipment}
            disabledForm={disabledForm}
            asset={asset}
            handleUpdateAssetField={handleUpdateAssetField}
          />
        </Grid>
        <Grid size={12}>
          <FieldsSection
            title="Configurable Fields"
            fullWidth
            fields={enabledFields.configurable || []}
            disabledForm={disabledForm}
          />
        </Grid>
        {isDraft && !project?.settings?.onsite_manifest && (
          <Grid size={12}>
            <FieldsPreReceive fullWidth disabledForm={disabledForm} />
          </Grid>
        )}
        {shouldShowStorageFields ? (
          <>
            <Grid size={12}>
              <FieldsSection
                title="Storage"
                fullWidth
                disabledForm={disabledForm}
                fields={enabledFields.storage || []}
              />
            </Grid>
            <ItemShipmentInfoFields project={project} shipmentItem={shipmentItem} />
          </>
        ) : (
          <Grid size={12}>
            <Button fullWidth variant="outlined" onClick={handleMarkAsReceived}>
              Mark as Received & Input Into Inventory
            </Button>
          </Grid>
        )}
        <Grid size={12}>
          <FieldsOrigin
            fullWidth
            display={display}
            asset={asset}
            disabledForm={disabledForm}
            colorsOptions={colorsOptions}
            manufacturersOptions={manufacturersOptions}
          />
        </Grid>
        <Grid size={12}>
          <FieldsDestination
            display={display}
            type={type}
            asset={asset}
            disabledForm={disabledForm}
            colorsOptions={colorsOptions}
            manufacturersOptions={manufacturersOptions}
          />
        </Grid>
      </Grid>
      {showDraftModal && (
        <DraftShipmentModal
          description={
            isOnsiteInventory
              ? "You're about to convert this onsite inventory shipment to received in warehouse.\nAre you sure?"
              : "You're about to mark this shipment as being received.\nAre you sure?"
          }
          cancelLabel={isOnsiteInventory ? "Cancel" : "Keep as Draft"}
          confirmLabel={isOnsiteInventory ? "Yes, Convert" : "Yes, Received"}
          onClose={() => setShowDraftModal(false)}
          onKeepAsDraft={handleKeepAsDraft}
          onMarkAsReceived={handleConfirmReceived}
        />
      )}
    </SustainabilitySheetProvider>
  );
}
